import React, { useEffect } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import {
  Slide,
  Grid,
  Typography,
  Button,
  Dialog,
  IconButton,
  Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import LocaleKeys from '../../../locales/keys';
import selectBackground from '../../../images/select-bg.png';
import postMessage from '../../../utils/postMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  dialogModal: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
    },
  },
  fieldSelectNative: {
    height: '10%',
    marginBottom: '15px',
    '&.MuiInputBase-root': {
      border: '1px solid #ccc',
      borderRadius: '4px',
      '&.Mui-focused': {
        borderColor: '#66afe9',
        boxShadow:
          'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102, 175, 233, 0.6)',
      },
    },
    '& .MuiNativeSelect-select': {
      height: '50%',
      color: '#555',
      fontSize: '14px',
      background: `url(${selectBackground}) no-repeat right center;`,
    },
  },
  subscriptionValidUntilInfo: {
    fontSize: '18px',
    fontWeight: 500,
    marginBottom: '24px',
  },
}));

const styles = (theme) => ({
  paper: {
    margin: '10px',
    maxWidth: '100%',
    position: 'fixed',
    top: 0,
    [theme.breakpoints.down('md')]: {
      maxWidth: '340px',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1.5),
    top: theme.spacing(0),
    color: theme.palette.custom.black,
    opacity: 0.2,
    paddingTop: '12px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  button: {
    color: theme.palette.custom.white,
    borderRadius: '5px',
    width: '240px',
    height: '37px',
    fontWeight: 500,
    letterSpacing: 0.5,
    padding: '12px 15px',
    lineHeight: 1,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  closeIcon: {
    fontSize: '21px',
    fontWeight: '700',
    lineHeight: 1,
    textShadow: `0 1px 0 ${theme.palette.custom.white}`,
    padding: '3px 0',
  },
  dialogTitle: {
    lineHeight: 1.42,
    fontFamily: 'Formula1-Black',
  },
  dialogContainer: {
    backgroundColor: '#fcf8e3',
  },
  cancelButton: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      boxShadow: '0 0 0 2px #e00 inset',
      background: theme.palette.custom.white,
      color: theme.palette.custom.lightBlack,
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: '20px',
    },
  },
  returnToSubscriptionButton: {
    backgroundColor: theme.palette.custom.darkGreyBg,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, handleClose, onUpdate, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h2" className={classes.dialogTitle}>
        {children}
      </Typography>
      {handleClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          data-testid="security-code-close-button"
          onClick={handleClose}
          disableRipple
          disableFocusRipple
          variant="text"
          size="small"
        >
          <CloseRoundedIcon className={classes.closeIcon} />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogModal = withStyles(styles)((props) => {
  const { children, classes, handleClose, open, ...other } = props;
  return (
    <Dialog
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-labelledby="subscription-dialog-title"
      classes={{ paper: classes.paper }}
      open={open}
      {...other}
    >
      {children}
    </Dialog>
  );
});

const DialogButtonAction = withStyles(styles)((props) => {
  const { children, classes, handleClose, name, onClick, ...other } = props;

  return (
    <Button
      autoFocus
      size="small"
      className={`${classes.button} ${classes.cancelButton}`}
      onClick={onClick}
      {...other}
    >
      {children}
    </Button>
  );
});

const DialogContent = withStyles(() => ({
  root: {
    padding: '15px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles(() => ({
  root: {
    margin: 0,
    padding: '15px',
  },
}))(MuiDialogActions);

export function PostCancellationView({
  open,
  subscriptionProps,
  handleClose,
  returnToMySubscription,
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  useEffect(() => {
    postMessage('module.showBackdrop');
  }, []);

  const postCancellationMessage1 = t(
    LocaleKeys.retention.postCancellationMessage1,
    {
      cycleDate: subscriptionProps?.cycleDate,
    },
  );
  const postCancellationMessage2 = t(
    LocaleKeys.retention.postCancellationMessage2,
    {},
  );

  return (
    <div>
      <DialogModal
        handleClose={() => {
          postMessage('module.hideBackdrop');
          handleClose();
        }}
        open={open}
        className={classes.dialogModal}
      >
        <DialogTitle
          id="post-cancellation-form-title"
          style={{
            padding: '15px',
            textTransform: 'uppercase',
            alignItems: 'center',
            justifyContent: ' center',
          }}
          handleClose={() => {
            postMessage('module.hideBackdrop');
            handleClose();
          }}
        >
          {t(LocaleKeys.retention.postCancellationTitle)}
        </DialogTitle>
        <DialogContent
          dividers
          style={{
            borderColor: '#e5e5e5',
          }}
        >
          <Grid container className={classes.root}>
            <Grid container>
              <Box component={Grid} item xs={12}>
                <Typography className={classes.subscriptionValidUntilInfo}>
                  {postCancellationMessage1}
                </Typography>
                <Typography className={classes.subscriptionValidUntilInfo}>
                  {postCancellationMessage2}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <DialogButtonAction
            disableRipple
            disableFocusRipple
            name="returnToSubscriptionButton"
            size="small"
            onClick={() => {
              postMessage('module.hideBackdrop');
              returnToMySubscription();
            }}
          >
            {t(LocaleKeys.retention.postCancellationBack)}
          </DialogButtonAction>
        </DialogActions>
      </DialogModal>
    </div>
  );
}

export default PostCancellationView;
